import { StandardFonts, degrees, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { OBJ } from '../constants';
import { TEXT_PAD, lineWidth, measureLineWidth, wrapTextLines } from './drawObjects';
import { runsOf } from './richText';

/**
 * Draw replacement text as real PDF text instead of a raster stamp.
 *
 * Stamping a paragraph as a PNG makes it visibly softer than the untouched
 * vector text beside it — a halo around every glyph that shows up badly in
 * print. Latin text set in one of the standard families can be written as text
 * operators instead: crisp at any zoom, selectable, searchable and far smaller.
 *
 * Layout still comes from the same canvas measurements the on-screen painter
 * uses, so the PDF matches the preview line for line; only the glyph rendering
 * changes. Anything the standard fonts cannot encode — Bangla above all — falls
 * back to the raster path.
 */

const STANDARD_FONTS = {
    helvetica: {
        regular: StandardFonts.Helvetica,
        bold: StandardFonts.HelveticaBold,
        italic: StandardFonts.HelveticaOblique,
        boldItalic: StandardFonts.HelveticaBoldOblique,
    },
    times: {
        regular: StandardFonts.TimesRoman,
        bold: StandardFonts.TimesRomanBold,
        italic: StandardFonts.TimesRomanItalic,
        boldItalic: StandardFonts.TimesRomanBoldItalic,
    },
    courier: {
        regular: StandardFonts.Courier,
        bold: StandardFonts.CourierBold,
        italic: StandardFonts.CourierOblique,
        boldItalic: StandardFonts.CourierBoldOblique,
    },
};

/**
 * Families a standard font can stand in for *without moving a glyph*.
 *
 * Only metric-compatible pairs belong here: Arimo/Tinos/Cousine are Google's
 * metric clones of Arial/Times/Courier, so the canvas measurements the layout
 * was built from still hold when the export draws the standard face instead.
 * Any other family (the whole rest of the catalogue) is stamped.
 */
const STANDARD_BY_FAMILY = {
    helvetica: 'helvetica',
    arial: 'helvetica',
    arimo: 'helvetica',
    'liberation sans': 'helvetica',
    times: 'times',
    'times new roman': 'times',
    tinos: 'times',
    'liberation serif': 'times',
    courier: 'courier',
    'courier new': 'courier',
    cousine: 'courier',
    'liberation mono': 'courier',
};

/** The standard-font set for a family name, or undefined for everything else. */
export const standardFontFor = (family) => (
    STANDARD_FONTS[STANDARD_BY_FAMILY[String(family || '').trim().toLowerCase()]]
);

/** WinAnsi covers Latin-1 plus a handful of punctuation the standard fonts map. */
const WIN_ANSI = /^[\t\n\r\x20-\x7E -ÿ€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]*$/;

const variantOf = (obj) => (obj.bold && obj.italic ? 'boldItalic' : obj.bold ? 'bold' : obj.italic ? 'italic' : 'regular');

const styleKey = (style) => [
    style.fontId || '',
    style.fontFamily,
    style.bold ? 1 : 0,
    style.italic ? 1 : 0,
].join('|');

/**
 * A font for every run of the object, or null if any run has none.
 *
 * Each run is tried against the document's own font first (an "Edit text" box)
 * and then against the standard fonts its family maps to. One run without a
 * font sends the whole box to the stamp path: half a paragraph as text and half
 * as an image would not line up.
 */
export async function resolveRunFonts(obj, { embedFont, embedDocumentFont }) {
    if (obj.type !== OBJ.TEXT || obj.rotation) return null;

    const fonts = new Map();
    for (const run of runsOf(obj)) {
        const { style } = run;
        const key = styleKey(style);
        if (fonts.has(key)) {
            if (!fonts.get(key)) return null;
            continue;
        }

        let font = null;
        if (style.fontId && embedDocumentFont) {
            // eslint-disable-next-line no-await-in-loop
            const candidate = await embedDocumentFont(style.fontId);
            if (candidate && embeddedFontMatches(candidate, { ...style, text: run.text })) font = candidate;
        }
        if (!font && !style.fontStack && standardFontFor(style.fontFamily) && WIN_ANSI.test(run.text)) {
            // eslint-disable-next-line no-await-in-loop
            font = await embedFont(style.fontFamily, variantOf(style));
        }

        fonts.set(key, font);
        if (!font) return null;
    }
    return fonts;
}

/**
 * Re-embed the document's OWN font so an edited paragraph stays real text.
 *
 * Stamping it as an image is what makes an edited paragraph stand out in the
 * download: the raster is anti-aliased, so the glyphs read a shade heavier than
 * the vector text beside them, and the patch's alpha edge shows as a faint
 * rectangle in viewers that interpolate it (Chrome's does). Embedding the font
 * program pdf.js already built for the page removes both — and keeps the text
 * selectable and searchable.
 *
 * `font.data` only survives on the main thread because the document is opened
 * with `fontExtraProperties`.
 */
export function createDocumentFontCache(outDoc, pdfjsDoc) {
    const cache = new Map();
    let commonObjs = null;
    let registered = false;

    return async (fontId) => {
        if (!fontId || !pdfjsDoc) return null;
        if (cache.has(fontId)) return cache.get(fontId);

        const embed = async () => {
            if (!commonObjs) commonObjs = (await pdfjsDoc.getPage(1)).commonObjs;
            if (!commonObjs.has(fontId)) return null;
            const data = commonObjs.get(fontId)?.data;
            if (!data?.byteLength) return null;
            if (!registered) {
                outDoc.registerFontkit(fontkit);
                registered = true;
            }
            // No subsetting: the program is already a subset of the original,
            // and re-subsetting it costs time for nothing.
            return outDoc.embedFont(data, { subset: false, customName: `Doc-${fontId}` });
        };

        const task = embed().catch(() => null);
        cache.set(fontId, task);
        return task;
    };
}

/**
 * Whether the embedded font really draws the text the canvas measured.
 *
 * A subset carries only the glyphs the document already used, so a word the
 * user typed can hit `.notdef` — invisible in the PDF while the canvas showed
 * it in a fallback face. Every distinct word is checked, because one bad word
 * in a paragraph is enough to make the export wrong.
 *
 * WORDS, never whole lines: these subsets routinely omit `space` (the page's
 * text operators position each run instead of spacing them), so a line's width
 * disagrees by a space's worth per gap even when every glyph is present. That
 * is also why the drawing below places words one at a time.
 */
/**
 * Whether the embedded program has a real glyph for every character.
 *
 * Whitespace is exempt: these subsets routinely omit `space`, because the
 * page's text operators position each run rather than spacing them, and the
 * drawing below places words one at a time for the same reason.
 */
function fontCoversText(font, text) {
    const program = font?.embedder?.font;
    if (typeof program?.hasGlyphForCodePoint !== 'function') return true;
    for (const ch of text) {
        if (/\s/.test(ch)) continue;
        try {
            if (!program.hasGlyphForCodePoint(ch.codePointAt(0))) return false;
        } catch {
            return false;
        }
    }
    return true;
}

export function embeddedFontMatches(font, obj) {
    const text = String(obj.text ?? '');
    // Ask the font program itself first. A subset answers this exactly, and it
    // has to be asked: a missing character does not throw and does not measure
    // zero — fontkit hands back `.notdef` with a default advance of ~0.6em,
    // which is close enough to a real letter's width to pass the measurement
    // below. That is how REPLACEDCLAUSE once exported as RERLACEDCLARSE.
    if (!fontCoversText(font, text)) return false;

    const words = [...new Set(text.split(/\s+/).filter(Boolean))];
    if (!words.length) return true;
    try {
        return words.every((word) => {
            const drawn = font.widthOfTextAtSize(word, obj.fontSize);
            const measured = measureLineWidth(obj, word);
            return drawn > 0 && measured > 0 && Math.abs(drawn / measured - 1) <= 0.02;
        });
    } catch {
        // An unencodable character throws rather than measuring.
        return false;
    }
}

export function hexToRgb(hex) {
    const value = String(hex || '#000000').replace('#', '');
    const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
    const int = parseInt(full.slice(0, 6), 16);
    if (!Number.isFinite(int)) return rgb(0, 0, 0);
    return rgb(((int >> 16) & 255) / 255, ((int >> 8) & 255) / 255, (int & 255) / 255);
}

/** Lazily embed (and cache) the standard fonts a document actually uses. */
export function createFontCache(pdfDoc) {
    const cache = new Map();
    return async (family, variant) => {
        const name = standardFontFor(family)?.[variant];
        if (!name) return null;
        if (!cache.has(name)) cache.set(name, await pdfDoc.embedFont(name));
        return cache.get(name);
    };
}

/**
 * @param {object} page pdf-lib page
 * @param {object} obj text object in page view space
 * @param {{ fonts: Map<string, object>,
 *   place: (u: number, v: number) => [number, number], rotation: number }} ctx
 *   `fonts` comes from `resolveRunFonts` — one entry per run style.
 */
export async function drawVectorText(page, obj, ctx) {
    const rotate = degrees(ctx.rotation);

    if (obj.background && obj.background !== 'transparent') {
        // Rect corners in view space, mapped through the page's rotation.
        const corners = [
            ctx.place(obj.x, obj.y),
            ctx.place(obj.x + obj.w, obj.y),
            ctx.place(obj.x + obj.w, obj.y + obj.h),
            ctx.place(obj.x, obj.y + obj.h),
        ];
        const xs = corners.map((c) => c[0]);
        const ys = corners.map((c) => c[1]);
        page.drawRectangle({
            x: Math.min(...xs),
            y: Math.min(...ys),
            width: Math.max(...xs) - Math.min(...xs),
            height: Math.max(...ys) - Math.min(...ys),
            color: hexToRgb(obj.background),
        });
    }

    const lines = wrapTextLines(obj);
    const inner = obj.w - TEXT_PAD * 2;
    let top = obj.y + TEXT_PAD;

    for (const line of lines) {
        const lineH = (line.fontSize || obj.fontSize) * (obj.lineHeight || 1.35);
        const baseline = top + (line.fontSize || obj.fontSize) * 0.82;
        top += lineH;
        if (!line.segments.length) continue;

        const measured = lineWidth(line);
        const gaps = obj.align === 'justify' && !line.last
            ? line.segments.filter((seg) => seg.space).length
            : 0;
        const slack = gaps && measured < inner ? (inner - measured) / gaps : 0;

        let pen = obj.x + TEXT_PAD;
        if (!slack) {
            if (obj.align === 'center') pen += (inner - measured) / 2;
            else if (obj.align === 'right') pen += inner - measured;
        }

        for (const seg of line.segments) {
            const { style } = seg;
            // An outline or a shadow is a painted effect; pdf-lib can set the
            // glyphs but not wear them, so the box goes back to being stamped.
            if (style.outline || style.shadow) return false;
            const font = ctx.fonts.get(styleKey(style));
            if (!font) return false;
            const width = measureLineWidth(style, seg.text);

            if (!seg.space) {
                // One word at a time, at the offset the canvas measured it at. A
                // document's own subset often has no `space` glyph, and drawing a
                // whole line would ask the font to encode one.
                let cursor = 0;
                for (const word of seg.text.split(' ').filter(Boolean)) {
                    const at = seg.text.indexOf(word, cursor);
                    const [x, y] = ctx.place(pen + measureLineWidth(style, seg.text.slice(0, at)), baseline);
                    page.drawText(word, {
                        x, y, size: style.fontSize, font, color: hexToRgb(style.color), rotate,
                    });
                    cursor = at + word.length;
                }
                if (style.underline) {
                    drawUnderline(page, ctx, style, pen, baseline, width, hexToRgb(style.color), rotate);
                }
                if (style.strike) {
                    drawStrike(page, ctx, style, pen, baseline, width, hexToRgb(style.color), rotate);
                }
            }
            pen += width + (seg.space ? slack : 0);
        }
    }

    return true;
}

/** The strike is the underline moved up onto the middle of the letters. */
function drawStrike(page, ctx, style, u, baseline, width, color, rotate) {
    const thickness = Math.max(0.4, style.fontSize * 0.06);
    const [x, y] = ctx.place(u, baseline - style.fontSize * 0.3 + thickness);
    page.drawRectangle({ x, y, width, height: thickness, color, rotate });
}

function drawUnderline(page, ctx, style, u, baseline, width, color, rotate) {
    const thickness = Math.max(0.4, style.fontSize * 0.06);
    // Anchor the rect's *bottom* edge in view space: pdf-lib grows a rectangle
    // along its local +y, which the page rotation maps back to "up the page".
    const [x, y] = ctx.place(u, baseline + style.fontSize * 0.16 + thickness);
    page.drawRectangle({ x, y, width, height: thickness, color, rotate });
}
